'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useState } from 'react';
import { buildBreadcrumbs } from '@/src/lib/navigation';

const MAX_VISIBLE_ITEMS = 3;

export function AppBreadcrumbs() {
  const pathname = usePathname();
  const items = buildBreadcrumbs(pathname);
  const [copied, setCopied] = useState(false);

  const shouldCollapse = items.length > MAX_VISIBLE_ITEMS + 1;
  let visibleItems = items;
  let collapsedCount = 0;
  if (shouldCollapse) {
    visibleItems = [
      items[0]!,
      ...items.slice(-MAX_VISIBLE_ITEMS),
    ];
    collapsedCount = items.length - visibleItems.length;
  }

  function handleCopyLink() {
    if (typeof navigator === 'undefined' || !navigator.clipboard) return;
    void navigator.clipboard.writeText(pathname).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }).catch(() => {
      // Clipboard write can fail (permissions, insecure context); ignore silently.
    });
  }

  return (
    <nav aria-label="Breadcrumb" className="hidden md:block">
      <ol className="flex flex-wrap items-center gap-1.5 text-sm text-[var(--k-color-text-secondary)]">
        {visibleItems.map((item, index) => {
          const isLast = index === visibleItems.length - 1;
          return (
            <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
              {index > 0 ? (
                <Chevron />
              ) : null}
              {item.href && !item.isCurrent ? (
                <Link
                  href={item.href}
                  className="rounded-md px-1.5 py-0.5 text-[var(--k-color-text-secondary)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
                  title={item.label}
                >
                  <span className="max-w-[20rem] truncate inline-block align-bottom">
                    {item.label}
                  </span>
                </Link>
              ) : (
                <span
                  className={
                    item.isCurrent
                      ? 'inline-flex max-w-[24rem] items-center gap-1.5 truncate rounded-md bg-[var(--k-color-surface-muted)] px-2 py-0.5 font-semibold text-[var(--k-color-text-primary)]'
                      : 'inline-block max-w-[20rem] truncate align-bottom'
                  }
                  title={item.label}
                >
                  {item.label}
                </span>
              )}
              {isLast ? (
                <button
                  type="button"
                  onClick={handleCopyLink}
                  aria-label="Pfad kopieren"
                  title={copied ? 'Kopiert!' : 'Pfad kopieren'}
                  className="ml-1 inline-flex h-6 w-6 items-center justify-center rounded text-[var(--k-color-text-muted)] transition hover:bg-[var(--k-color-surface-muted)] hover:text-[var(--k-color-text-primary)]"
                >
                  {copied ? (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  ) : (
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                  )}
                </button>
              ) : null}
            </li>
          );
        })}
        {shouldCollapse ? (
          <li className="flex items-center gap-1.5">
            <Chevron />
            <span className="text-xs text-[var(--k-color-text-faint)]" title={`${collapsedCount} weitere Ebene(n)`}>
              +{collapsedCount}
            </span>
          </li>
        ) : null}
      </ol>
    </nav>
  );
}

function Chevron() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0 text-[var(--k-color-text-faint)]"
    >
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}